import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  TextField,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import AddToQueueRoundedIcon from "@mui/icons-material/AddToQueueRounded";
import * as Constants from './api/constants';
import {
  startOfDay,
  endOfWeek,
  endOfMonth,
  endOfYear,
  addDays,
  addMonths,
  addYears,
} from "date-fns";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import mqttLogo from '../components/resources/mqtt-logo.png';
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";

/** ---------- helpers ---------- **/
function sum(arr) {
  return arr.reduce((a, b) => a + b, 0);
}

function fmtCompact(n) {
  if (n >= 1_000_000) return `${Math.round(n / 100_000) / 10}M`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return `${n}`;
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function toISODateInput(d) {
  // d: Date -> "YYYY-MM-DD"
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseISODateInput(s) {
  // "YYYY-MM-DD" -> Date (local)
  const [y, m, d] = s.split("-").map((x) => Number(x));
  return new Date(y, (m || 1) - 1, d || 1);
}

function daysDiff(a, b) {
  const ms = 24 * 60 * 60 * 1000;
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.floor((end - start) / ms);
}

function fmtLabel(date) {
  // "Jan 5"
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtMonth(date) {
  return date.toLocaleDateString(undefined, { month: "short" });
}

function makeSeries(mode, from, to) {
  if (mode === "daily") {
    const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const users = labels.map(() => randInt(30, 140));
    const devices = labels.map(() => randInt(20, 120));
    return labels.map((label, i) => ({ label, users: users[i], devices: devices[i] }));
  }

  if (mode === "weekly") {
    const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const users = labels.map(() => randInt(30, 140));
    const devices = labels.map(() => randInt(20, 120));
    return labels.map((label, i) => ({ label, users: users[i], devices: devices[i] }));
  }

  if (mode === "monthly") {
    const now = new Date();
    const y = now.getFullYear();
    const labels = Array.from({ length: 12 }, (_, i) => fmtMonth(new Date(y, i, 1))); // Jan..Dec
    const users = labels.map(() => randInt(900, 2600));
    const devices = labels.map(() => randInt(700, 2400));
    return labels.map((label, i) => ({ label, users: users[i], devices: devices[i] }));
  }

  if (mode === "yearly") {
    const now = new Date();
    const year = now.getFullYear();
    const labels = Array.from({ length: 5 }, (_, i) => String(year - 4 + i)); // last 5 years
    const users = labels.map(() => randInt(12_000, 36_000));
    const devices = labels.map(() => randInt(10_000, 34_000));
    return labels.map((label, i) => ({ label, users: users[i], devices: devices[i] }));
  }

  // custom: bucket between from/to, capped to 21 points
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  const days = Math.max(1, daysDiff(start, end) + 1);

  const maxPoints = 21;
  const step = Math.ceil(days / maxPoints);

  const rows = [];
  for (let i = 0; i < days; i += step) {
    const d0 = addDays(start, i);
    const d1 = addDays(start, Math.min(i + step - 1, days - 1));
    const label = step === 1 ? fmtLabel(d0) : `${fmtLabel(d0)}-${d1.getDate()}`;
    rows.push({
      label,
      users: randInt(60 * step, 180 * step),
      devices: randInt(40 * step, 160 * step),
    });
  }
  return rows;
}

function pctChange(current, previous) {
  if (previous <= 0) return { pct: 0, sign: "0" };
  const p = ((current - previous) / previous) * 100;
  return { pct: Math.round(p * 10) / 10, sign: p > 0 ? "+" : p < 0 ? "-" : "0" };
}


const getDeltaChip = (value) => ({
  label: `${value > 0 ? "+" : value < 0 ? "-" : ""}${Math.abs(value)}`,
  tone: value > 0 ? "success" : value < 0 ? "warning" : "default",
});

const getDeltaTooltip = (value, type) => {
  if (!value) return "No change";

  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const formatted = sign + Math.abs(value);

  switch (type) {
    case "daily":
      return formatted + " in the last 7 days";
    case "weekly":
      return formatted + " in the last 12 weeks";
    case "monthly":
      return formatted + " in the last 12 months";
    case "yearly":
      return formatted + " in the last 10 years (≥ 2022)";
    case "custom":
      return formatted + " in the selected period";
    default:
      return formatted;
  }
};

function StatCard({ icon, label, value, sub, chip, chipValue, type }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        bgcolor: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <CardContent sx={{ p: 2.25, height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {/* ICON + CONTENT */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ minWidth: 0, flex: 1 }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.05)",
                color: "rgba(50,116,162,1)",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            {/* Label/Value/Sub Column */}
            <Stack direction="column" spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.2, textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {label}
              </Typography>

              <Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {value}
              </Typography>

              {sub && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {sub}
                </Typography>
              )}
            </Stack>
          </Stack>

          {chip && (
            <Tooltip title={getDeltaTooltip(chipValue, type)} arrow>
              <span>
                <Chip
                  size="small"
                  color={chip.tone || "default"}
                  label={chip.label}
                  variant="outlined"
                  sx={{ borderRadius: 999, flexShrink: 0 }}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function makeSeriesWithMqtt(mode, from, to) {
  const base = makeSeries(mode, from, to);

  const oldRandom = Math.random;
  const rows = base.map((r) => {
    const ratio = 0.35 + Math.random() * 0.4;
    const jitter = 0.9 + Math.random() * 0.2;
    const mqttDevices = Math.max(0, Math.round(r.devices * ratio * jitter));
    return { ...r, mqttDevices };
  });
  Math.random = oldRandom;

  return rows;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function makeAuthPie(totalUsers) {
  const googleShare = clamp(0.45 + Math.random() * 0.2, 0.35, 0.7);
  const appleShare = clamp(0.12 + Math.random() * 0.12, 0.08, 0.3);
  const emailShare = clamp(1 - googleShare - appleShare, 0.05, 0.55);

  const sumShare = googleShare + appleShare + emailShare;

  const google = Math.round((totalUsers * googleShare) / sumShare);
  const apple = Math.round((totalUsers * appleShare) / sumShare);
  const email = Math.max(0, totalUsers - google - apple);

  return [
    { name: "Google", value: google },
    { name: "Apple", value: apple },
    { name: "Email", value: email },
  ];
}

function makeDeviceConnectedPie(totalUsers) {
  const withShare = clamp(0.55 + Math.random() * 0.3, 0.3, 0.95);
  const withDevice = Math.round(totalUsers * withShare);
  const withoutDevice = Math.max(0, totalUsers - withDevice);

  return [
    { name: "With device(s)", value: withDevice },
    { name: "No devices", value: withoutDevice },
  ];
}

// Request count: 
const TIMEZONE = "Australia/Sydney";

function requestCountFormatXAxis(timestamp, granularity) {
  const d = new Date(timestamp).toLocaleString("en-AU", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  if (granularity === "day") return d.split(",")[0];
  if (granularity === "hour") return d.split(",")[1].trim();
  return d;
}

function requestCountTooltipLabelFormatter(timestamp) {
  return new Date(timestamp).toLocaleString("en-AU", {
    timeZone: TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function getGranularity(fromStr, toStr) {
  const from = new Date(fromStr);
  const to = new Date(toStr);
  const diffDays = (to - from) / (1000 * 60 * 60 * 24);

  if (diffDays <= 2) return "hour";
  if (diffDays <= 60) return "day";
  if (diffDays <= 365) return "week";
  return "month";
}

function aggregateData(data, granularity) {
  const map = new Map();

  data.forEach((item) => {
    const d = new Date(item.time);

    let key;

    if (granularity === "hour") {
      d.setMinutes(0, 0, 0);
      key = d.getTime();
    }

    if (granularity === "day") {
      d.setHours(0, 0, 0, 0);
      key = d.getTime();
    }

    if (granularity === "week") {
      const day = d.getDay();
      const diff = d.getDate() - day;
      const weekStart = new Date(d.setDate(diff));
      weekStart.setHours(0, 0, 0, 0);
      key = weekStart.getTime();
    }

    if (granularity === "month") {
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
      key = d.getTime();
    }

    map.set(key, (map.get(key) || 0) + item.value);
  });

  return Array.from(map.entries())
    .map(([time, value]) => ({ time: Number(time), value }))
    .sort((a, b) => a.time - b.time);
}

export default function AdminDashboard() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [mode, setMode] = React.useState("daily"); // daily || weekly | monthly | yearly | custom
  const [segment, setSegment] = React.useState("all");
  const [seed, setSeed] = React.useState(1);

  const [fromStr, setFromStr] = React.useState(toISODateInput(addDays(new Date(), -14)));
  const [toStr, setToStr] = React.useState(toISODateInput(new Date()));

  const fromRef = React.useRef(null);
  const toRef = React.useRef(null);

  const openPicker = (ref) => {
    if (ref.current && ref.current.showPicker) {
      ref.current.showPicker();
    } else if (ref.current) {
      ref.current.focus();
    }
  };

  const from = React.useMemo(() => parseISODateInput(fromStr), [fromStr]);
  const to = React.useMemo(() => parseISODateInput(toStr), [toStr]);

  const [loading, setLoading] = React.useState(false);
  const [apiData, setApiData] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const typeMap = {
        weekly: 1,
        monthly: 2,
        yearly: 3,
        custom: 4,
        daily: 5,
      };

      const requestOptions = {
        method: 'GET',
        credentials: "include"
      };

      const params = new URLSearchParams({
        type: typeMap[mode],
        startDate: fromStr,
        endDate: toStr,
      });

      const res = await fetch(Constants.baseURL + Constants.apiAdminDashboard + `?${params.toString()}`, requestOptions);
      const json = await res.json();
      setApiData(json);
      setLoading(false);
    };

    fetchData();
  }, [mode, fromStr, toStr]);

  const data = React.useMemo(() => {
    if (!apiData) return [];

    if (mode === "daily") return apiData.daily ?? [];
    if (mode === "weekly") return apiData.weekly ?? [];
    if (mode === "monthly") return apiData.monthly ?? [];
    if (mode === "yearly") return apiData.yearly ?? [];
    if (mode === "custom") return apiData.custom ?? [];

    return [];
  }, [apiData, mode]);

  const pad2 = (n) => String(n).padStart(2, "0");
  const toISO = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const endOfWeek = (d) => {
    const x = startOfDay(d);
    const day = x.getDay();
    const diff = 6 - day;
    return addDays(x, diff + 1);
  };

  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);

  const addMonths = (d, months) => {
    const y = d.getFullYear();
    const m = d.getMonth() + months;

    const newY = y + Math.floor(m / 12);
    const newM = ((m % 12) + 12) % 12;

    const day = d.getDate();
    const lastDay = new Date(newY, newM + 1, 0).getDate();
    return new Date(newY, newM, Math.min(day, lastDay));
  };

  const calcRangeByMode = (mode, now = new Date()) => {
    const today = startOfDay(now);

    if (mode === "daily") {
      const end = today;
      const start = addDays(end, -7);
      return { fromStr: toISO(start), toStr: toISO(end) };
    }

    if (mode === "weekly") {
      const end = endOfWeek(addDays(today, -7));
      const start = addDays(end, -(12 * 7) + 1);
      return { fromStr: toISO(start), toStr: toISO(end) };
    }

    if (mode === "monthly") {
      const end = endOfMonth(addMonths(today, -1));
      const start = addMonths(new Date(end.getFullYear(), end.getMonth(), 1), -11);
      return { fromStr: toISO(start), toStr: toISO(end) };
    }

    if (mode === "yearly") {
      const end = endOfYear(addYears(today, -1));
      const minYear = 2022;
      const calculatedStartYear = end.getFullYear() - 9;
      const startYear = Math.max(calculatedStartYear, minYear);
      const start = new Date(startYear, 0, 1);
      return { fromStr: toISO(start), toStr: toISO(end) };
    }

    return null;
  };

  React.useEffect(() => {
    const r = calcRangeByMode(mode);
    if (!r) return;
    setFromStr(r.fromStr);
    setToStr(r.toStr);
  }, [mode]);

  const handleFromChange = (v) => {
    setFromStr(v);
    if (mode !== "custom") setMode("custom");
  };

  const handleToChange = (v) => {
    setToStr(v);
    if (mode !== "custom") setMode("custom");
  };

  const kpis = React.useMemo(() => {
    if (!apiData) {
      return {
        totalUsers: 0,
        newUsers: 0,
        totalDevices: 0,
        newDevices: 0,
        newUsersDelta: { pct: 0, sign: "0" },
        newDevicesDelta: { pct: 0, sign: "0" },
      };
    }

    return {
      totalUsers: apiData.totalUsers,
      newUsers: apiData.totalNewUsers,
      totalDevices: apiData.totalDevices,
      newDevices: apiData.totalNewDevices,
      totalNewUsersWithDevics: apiData.totalNewUsersWithDevices,

      newUsersDelta: { pct: apiData.newUsersDelta.pct, sign: apiData.newUsersDelta.sign },
      newDevicesDelta: { pct: apiData.newDevicesDelta.pct, sign: apiData.newDevicesDelta.sign },
    };
  }, [apiData]);

  const authPie = React.useMemo(() => {
    if (!apiData) return [];

    return [
      { name: "Google", value: apiData.usersBySignIn.google },
      { name: "Apple", value: apiData.usersBySignIn.apple },
      { name: "Email", value: apiData.usersBySignIn.email },
    ];
  }, [apiData]);

  const deviceConnPie = React.useMemo(() => {
    if (!apiData) return [];

    return [
      { name: "With device(s)", value: apiData.usersWithDevices.withDevices },
      { name: "No devices", value: apiData.usersWithDevices.withoutDevices },
    ];
  }, [apiData]);

  const pies = React.useMemo(() => {
    const oldRandom = Math.random;
    let s = (seed + 123) * 99991;
    Math.random = () => {
      s = (s * 48271) % 0x7fffffff;
      return s / 0x7fffffff;
    };

    const auth = makeAuthPie(kpis.totalUsers);
    const deviceConn = makeDeviceConnectedPie(kpis.totalUsers);

    Math.random = oldRandom;

    return { auth, deviceConn };
  }, [kpis.totalUsers, seed]);

  // Request Count: 
  const [reqFromStr, setReqFromStr] = React.useState(toISODateInput(addDays(new Date(), -8)));
  const [reqToStr, setReqToStr] = React.useState(toISODateInput(addDays(new Date(), -1)));

  const reqFromRef = React.useRef(null);
  const reqToRef = React.useRef(null);

  const [requestData, setRequestData] = React.useState([]);

  const getTimeGranularity = (fromStr, toStr) => {
    const from = new Date(fromStr);
    const to = new Date(toStr);

    const totalDays = (to - from) / (1000 * 60 * 60 * 24);

    if (totalDays <= 2) {
      return "hourly";     // 1–2 days
    }

    if (totalDays <= 30) {
      return "daily";      // 3–30 days
    }

    if (totalDays <= 365) {
      return "monthly";    // up to 12 months
    }

    return "yearly";       // multi-year
  };

  const formatXAxis = (time, granularity) => {
    const d = new Date(time);

    switch (granularity) {
      case "hour":
        return `${String(d.getHours()).padStart(2, "0")}:00`;
      case "day":
        return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
      case "month":
        return d.toLocaleDateString([], { month: "short", year: "numeric" });
      case "year":
        return d.getFullYear();
      default:
        return d.toLocaleDateString();
    }
  };

  const granularity = React.useMemo(
    () => getGranularity(reqFromStr, reqToStr),
    [reqFromStr, reqToStr]
  );
  const processedData = React.useMemo(() => {
    const from = new Date(reqFromStr);
    const to = new Date(reqToStr);

    if (isNaN(from) || isNaN(to)) return [];

    to.setHours(23, 59, 59, 999);

    const filtered = requestData.filter(item => {
      const time = new Date(item.time).getTime();
      return time >= from.getTime() && time <= to.getTime();
    });

    return aggregateData(filtered, granularity);

  }, [requestData, reqFromStr, reqToStr, granularity]);

  const buildDailyTicks = (fromStr, toStr) => {
    const start = new Date(fromStr);
    start.setHours(0, 0, 0, 0);

    const end = new Date(toStr);
    end.setHours(0, 0, 0, 0);

    const ticks = [];
    const cursor = new Date(start);

    while (cursor <= end) {
      ticks.push(cursor.getTime());
      cursor.setDate(cursor.getDate() + 1);
    }

    return ticks;
  };

  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const buildTicks = (fromDate, toDate, granularity) => {
    const ticks = [];

    let current = parseLocalDate(fromDate);
    const end = parseLocalDate(toDate);


    while (current <= end) {

      ticks.push(current.getTime());


      if (granularity === "hour") {
        current.setHours(current.getHours() + 1);
      }
      else if (granularity === "day") {
        current.setDate(current.getDate() + 1);
      }
      else if (granularity === "month") {
        current.setMonth(current.getMonth() + 1);
      }
      else if (granularity === "year") {
        current.setFullYear(current.getFullYear() + 1);
      }
    }

    return ticks;
  };

  const fetchRequestCounts = async (fromDate = reqFromStr, toDate = reqToStr) => {
    try {
      const totalSeconds = (to.getTime() - from.getTime()) / 1000;

      let periodSeconds;
      if (totalSeconds <= 86400) {
        periodSeconds = 60;           // 1 day
      } else if (totalSeconds <= 86400 * 7) {
        periodSeconds = 300;          // 7 days
      } else if (totalSeconds <= 86400 * 30) {
        periodSeconds = 900;          // 30 days
      } else if (totalSeconds <= 86400 * 90) {
        periodSeconds = 3600;         // 90 days
      } else if (totalSeconds <= 86400 * 365) {
        periodSeconds = 21600;        // 1 year
      } else {
        periodSeconds = 86400;        // multi-year
      }

      const requestOptions = {
        method: 'GET',
        credentials: "include"
      };

      const params = new URLSearchParams({
        startDate: fromDate,
        endDate: toDate,
        periodSeconds: periodSeconds
      });

      const res = await fetch(Constants.baseURL + Constants.apiAdminDashboardRequestCount + `?${params.toString()}`, requestOptions);
      const json = await res.json();

      const chartData = json.map((point) => ({
        time: new Date(point.time).getTime(),
        value: point.value,
      }));

      setRequestData(chartData);
    } catch (err) {
      console.error("Failed to fetch request counts:", err);
    }
  };

  const [drillOpen, setDrillOpen] = React.useState(false);
  const [drillData, setDrillData] = React.useState([]);
  const [drillGranularity, setDrillGranularity] = React.useState(null);
  const [drillTitle, setDrillTitle] = React.useState("");

  const handleBarClick = (state) => {
    if (!state || !state.activePayload?.length) return;

    const data = state.activePayload[0].payload;

    setSelectedStatusData(data);
    setStatusDialogOpen(true);
  };

  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [selectedStatusData, setSelectedStatusData] = React.useState(null);

  const popupChartData = React.useMemo(() => {
    if (!selectedStatusData) return [];

    return [
      {
        type: "Total",
        online: selectedStatusData.totalOnline,
        offline: selectedStatusData.totalOffline,
      },
      {
        type: "V1/V2",
        online: selectedStatusData.v1v2Online,
        offline: selectedStatusData.v1v2Offline,
      },
      {
        type: "V3",
        online: selectedStatusData.v3Online,
        offline: selectedStatusData.v3Offline,
      },
    ];
  }, [selectedStatusData]);

  const handlePointClick = (data) => {
    const clickedDate = new Date(data.time);

    let from;
    let to;

    if (granularity === "year") {
      // Drill to full month
      from = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), 1);
      to = new Date(clickedDate.getFullYear(), clickedDate.getMonth() + 1, 0);
    }

    else if (granularity === "month") {
      // Drill to full day
      from = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate(), 0, 0, 0);
      to = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate(), 23, 59, 59);
    }

    else if (granularity === "day") {
      // Drill to hourly (1 hour window)
      from = new Date(clickedDate);
      to = new Date(clickedDate);
      to.setHours(clickedDate.getHours() + 1);
    }

    setReqFromStr(from.toISOString().split("T")[0]);
    setReqToStr(to.toISOString().split("T")[0]);

    fetchRequestCounts();
  };

  React.useEffect(() => {
    fetchRequestCounts();

    // const intervalId = setInterval(() => {
    //   console.log('Refreshing...');
    //   fetchRequestCounts();
    // }, 30000);

    // return () => clearInterval(intervalId);
  }, []);

  const handleDrillDown = (data) => {
    const clickedDate = new Date(data.time);

    let newGranularity;
    let start;
    let end;
    let title;

    if (granularity === "year") {
      newGranularity = "month";

      start = new Date(clickedDate.getFullYear(), 0, 1);
      end = new Date(clickedDate.getFullYear(), 11, 31, 23, 59, 59);

      title = `Monthly breakdown - ${clickedDate.getFullYear()}`;
    }

    else if (granularity === "month") {
      newGranularity = "day";

      start = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), 1);
      end = new Date(clickedDate.getFullYear(), clickedDate.getMonth() + 1, 0, 23, 59, 59);

      title = `Daily breakdown - ${clickedDate.toLocaleString("default", { month: "long", year: "numeric" })}`;
    }

    else {
      newGranularity = "hour";

      start = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate(), 0, 0, 0);
      end = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate(), 23, 59, 59);

      title = `Hourly breakdown - ${clickedDate.toLocaleDateString()}`;
    }

    const filtered = requestData.filter(x => {
      const d = new Date(x.time);
      return d >= start && d <= end;
    });

    const aggregated = aggregateData(filtered, newGranularity);

    setDrillGranularity(newGranularity);
    setDrillData(aggregated);
    setDrillTitle(title);
    setDrillOpen(true);
  };

  const parseUTCDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  };

  // Request count end -- 

  // Total devices - online/offline
  const deviceStatusData = React.useMemo(() => {
    if (!apiData?.deviceStatusSeries || apiData.deviceStatusSeries.length === 0)
      return [];

    return apiData.deviceStatusSeries.map((item) => {
      const date = new Date(item.label);
      let name;
      switch (mode) {
        case "daily":
          name = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
          break;
        case "weekly":
          name = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
          break;
        case "monthly":
          name = date.toLocaleDateString(undefined, { month: "short" });
          break;
        case "yearly":
          name = date.getFullYear().toString();
          break;
        default:
          name = date.toLocaleDateString();
          break;
      }

      return {
        name,
        label: date,
        // Total
        totalOnline: item.online,
        totalOffline: item.offline,
        // V1V2
        v1v2Online: item.v1V2Online,
        v1v2Offline: item.v1V2Offline,
        // V3
        v3Online: item.v3Online,
        v3Offline: item.v3Offline,
      };
    });
  }, [apiData, mode]);

  const controlH = 44;
  const isCustom = mode === "custom";

  const getDeltaChip = (value) => ({
    label: `${value > 0 ? "+" : value < 0 ? "-" : ""}${Math.abs(value)}`,
    tone: value > 0 ? "success" : value < 0 ? "warning" : "default",
  });

  const renderTooltip = (labelName) => (
    <RTooltip
      cursor={{ fill: 'transparent' }}
      labelFormatter={(value) => {
        const date = new Date(value);

        if (mode === "monthly") {
          return date.toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          });
        }

        if (mode === "yearly") {
          return date.getFullYear();
        }

        if (mode === "weekly" || mode === "daily") {
          return date.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }

        return date.toLocaleDateString();
      }}
      formatter={(value) => [`${value}`, labelName]}
      contentStyle={{
        background: "white",
        border: "1px solid #ccc",
        borderRadius: 4,
        padding: 8,
      }}
      itemStyle={{
        color: "#46505A",
      }}
      labelStyle={{
        color: "black",
        fontWeight: 600,
      }}
    />
  );

  const renderDeviceStatusTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    const headingStyle = {
      margin: "6px 0 2px 0",
      fontWeight: 600,
      color: 'black'
    };

    const onlineStyle = {
      margin: 0,
      color: "#4caf50",
    };

    const offlineStyle = {
      margin: 0,
      color: "#ef5350",
    };

    return (
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #ccc",
          padding: "10px",
        }}
      >
        {/* Label */}
        <p style={{ margin: "0 0 6px 0", fontWeight: 600, color: 'black' }}>
          {label}
        </p>

        {/* TOTAL */}
        <p style={headingStyle}>Total</p>
        <p style={onlineStyle}>Online: {data.totalOnline}</p>
        <p style={offlineStyle}>Offline: {data.totalOffline}</p>

        {/* V1/V2 */}
        <p style={headingStyle}>V1/V2</p>
        <p style={onlineStyle}>Online: {data.v1v2Online}</p>
        <p style={offlineStyle}>Offline: {data.v1v2Offline}</p>

        {/* V3 */}
        <p style={headingStyle}>V3</p>
        <p style={onlineStyle}>Online: {data.v3Online}</p>
        <p style={offlineStyle}>Offline: {data.v3Offline}</p>
      </div>
    );
  };

  const renderDeviceStatusDrilldownTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const point = payload[0]?.payload;
    if (!point) return null;

    return (
      <div
        style={{
          background: "white",
          border: "1px solid #ccc",
          borderRadius: 4,
          padding: 8,
        }}
      >
        {payload.map((entry, index) => (
          <div key={index} style={{ color: entry.fill || entry.color }}>
            {entry.name}: {entry.value}
          </div>
        ))}
      </div>
    );
  };

  const renderFirmwareTooltip = () => (
    <RTooltip
      cursor={{ fill: "transparent" }}
      content={({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;

        const data = payload[0].payload;

        const online = data.onlineCount ?? 0;
        const offline = data.offlineCount ?? 0;
        const total = online + offline;

        const onlinePct = total ? ((online / total) * 100).toFixed(1) : 0;
        const offlinePct = total ? ((offline / total) * 100).toFixed(1) : 0;

        return (
          <div
            style={{
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 4,
              padding: 10,
            }}
          >
            <div style={{ fontWeight: 600, color: "black", marginBottom: 4 }}>
              Firmware: {label}
            </div>

            <div style={{ color: "#46505A" }}>
              Total: {online + offline}
            </div>

            <div style={{ color: "#4caf50" }}>
              Online: {online} ({onlinePct}%)
            </div>

            <div style={{ color: "#ef5350" }}>
              Offline: {offline} ({offlinePct}%)
            </div>
          </div>
        );
      }}
    />
  );

  const firmwareOnlineOfflineData = apiData?.firmwareOfflineSeries || [];
  const hasFirmwareOnlineOfflineData = firmwareOnlineOfflineData.length > 0;
  const allFirmwares = firmwareOnlineOfflineData?.map(x => x.firmware) || [];
  const [selectedFirmwares, setSelectedFirmwares] = React.useState([]);

  React.useEffect(() => {
    if (firmwareOnlineOfflineData?.length) {
      setSelectedFirmwares(
        firmwareOnlineOfflineData.map((x) => x.firmware)
      );
    }

    if (allFirmwares.length > 0) {
      setSelectedFirmwares(allFirmwares);
    }
  }, [firmwareOnlineOfflineData]);

  const filteredFirmwareData = firmwareOnlineOfflineData.filter((x) =>
    selectedFirmwares.includes(x.firmware)
  );

  const handleToggleFirmware = (firmware) => {
    setSelectedFirmwares((prev) =>
      prev.includes(firmware)
        ? prev.filter((f) => f !== firmware)
        : [...prev, firmware]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedFirmwares(checked ? allFirmwares : []);
  };

  return (
    <Box sx={{ minHeight: "100vh", py: 3 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ width: "100%", mb: 2 }}>
          <Box
            sx={{
              maxWidth: (theme) => theme.breakpoints.values.lg,
              mx: "auto",
              width: "100%",
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              p: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Select */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <Select
                    value={mode}
                    onChange={(e) => setMode(String(e.target.value))}
                    sx={{ height: 45, borderRadius: 2 }}
                  >
                    <MenuItem value="daily">Daily (7 Days)</MenuItem>
                    <MenuItem value="weekly">Weekly (12 Weeks)</MenuItem>
                    <MenuItem value="monthly">Monthly (12 Months)</MenuItem>
                    <MenuItem value="yearly">Yearly (10 Years, ≥ 2022)</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* From */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={fromStr}
                  onChange={(e) => handleFromChange(e.target.value)}
                  inputRef={fromRef}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& input::-webkit-calendar-picker-indicator": {
                      display: "none",
                    },
                    "& input": {
                      appearance: "textfield", // Firefox
                    },
                  }}
                  inputProps={{
                    onClick: () => openPicker(fromRef),
                    style: { cursor: "pointer" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => openPicker(fromRef)}
                          edge="end"
                        >
                          <CalendarMonthIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* To */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={toStr}
                  onChange={(e) => handleToChange(e.target.value)}
                  inputRef={toRef}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& input::-webkit-calendar-picker-indicator": {
                      display: "none",
                    },
                    "& input": {
                      appearance: "textfield", // Firefox
                    },
                  }}
                  inputProps={{
                    onClick: () => openPicker(toRef),
                    style: { cursor: "pointer" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => openPicker(toRef)}
                          edge="end"
                        >
                          <CalendarMonthIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* KPIs */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <StatCard
              icon={<PeopleRoundedIcon />}
              label="Users with Devices"
              value={fmtCompact(apiData?.usersWithDevices?.withDevices ?? 0)}
              sub="All time"
              chip={getDeltaChip(kpis.totalNewUsersWithDevics)}
              chipValue={kpis.totalNewUsersWithDevics}
              type={mode}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={4}>
            <StatCard
              icon={<DevicesRoundedIcon />}
              label="Total Devices"
              value={fmtCompact(kpis.totalDevices)}
              sub="All time"
              chip={getDeltaChip(kpis.newDevices)}
              chipValue={kpis.newDevices}
              type={mode}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={4}>
            <StatCard
              icon={<PeopleRoundedIcon />}
              label="Total Users"
              value={fmtCompact(kpis.totalUsers)}
              sub="All time"
              chip={getDeltaChip(kpis.newUsers)}
              chipValue={kpis.newUsers}
              type={mode}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {/* New Users chart */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      New Users with Devices
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {mode === "weekly"
                        ? "Weekly"
                        : mode === "monthly"
                          ? "Monthly"
                          : mode === "yearly"
                            ? "Yearly"
                            : mode === "daily"
                              ? "Daily"
                              : "Custom"}
                    </Typography>
                  </Box>
                </Stack>

                {/* <Divider sx={{ mb: 2 }} /> */}

                <Box sx={{ width: "100%", height: 320, mt: 3 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} barCategoryGap="20%">
                      <XAxis
                        dataKey="label"
                        tickFormatter={(value) => {
                          const date = new Date(value);

                          if (mode === "monthly") {
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                            });
                          }

                          if (mode === "yearly") {
                            return date.getFullYear();
                          }

                          if (mode === "weekly" || mode === "daily") {
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            });
                          }

                          return date.toLocaleDateString();
                        }}
                      />
                      <YAxis />
                      {renderTooltip("New Users")}
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />
                      <Bar dataKey="newUsers" name="New users" fill={"#07a7ff"} radius={[10, 10, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* New Devices chart */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      New Devices
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {mode === "weekly"
                        ? "Weekly"
                        : mode === "monthly"
                          ? "Monthly"
                          : mode === "yearly"
                            ? "Yearly"
                            : mode === "daily"
                              ? "Daily"
                              : "Custom"}
                    </Typography>
                  </Box>
                </Stack>

                {/* <Divider sx={{ mb: 2 }} /> */}

                <Box sx={{ width: "100%", height: 320, mt: 3 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} barCategoryGap="20%">
                      <XAxis
                        dataKey="label"
                        tickFormatter={(value) => {
                          const date = new Date(value);

                          if (mode === "monthly") {
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                            });
                          }

                          if (mode === "yearly") {
                            return date.getFullYear();
                          }

                          if (mode === "weekly" || mode === "daily") {
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            });
                          }

                          return date.toLocaleDateString();
                        }}
                      />
                      <YAxis />
                      {renderTooltip("New Devices")}
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />
                      <Bar dataKey="newDevices" name="New devices" fill={"#07a7ff"} radius={[10, 10, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Device online offline status */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                height: "100%",
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Device Status
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {mode === "weekly"
                    ? "Weekly"
                    : mode === "monthly"
                      ? "Monthly"
                      : mode === "yearly"
                        ? "Yearly"
                        : mode === "daily"
                          ? "Daily"
                          : "Custom"}
                </Typography>

                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={deviceStatusData}
                      barCategoryGap={mode === "daily" ? "60%" : "20%"}
                      onClick={handleBarClick}
                    >
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <RTooltip content={renderDeviceStatusTooltip} cursor={{ fill: "transparent" }} />
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />

                      {/* Total */}
                      <Bar dataKey="totalOffline" stackId="total" fill="#ef5350" name="Total Offline" barSize={38} />
                      <Bar dataKey="totalOnline" stackId="total" fill="#4caf50" name="Total Online" barSize={38} />

                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Dialog
            open={statusDialogOpen}
            onClose={() => setStatusDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                bgcolor: "#1e1e1e",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 3,
              },
            }}
          >
            <DialogTitle sx={{ fontWeight: 500 }}>
              Device Status - {selectedStatusData?.name}
            </DialogTitle>

            <DialogContent sx={{ overflow: "visible" }}>
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={popupChartData}>
                    <XAxis dataKey="type" />
                    <YAxis allowDecimals={false} />
                    <RTooltip content={renderDeviceStatusDrilldownTooltip} cursor={{ fill: "transparent" }} />
                    <Legend />

                    <Bar dataKey="offline" stackId="status" fill="#ef5350" name="Offline" barSize={38} />
                    <Bar dataKey="online" stackId="status" fill="#4caf50" name="Online" barSize={38} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </DialogContent>
          </Dialog>

          {/*V3 Offline devices by firmware*/}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                height: "100%",
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mb: 1.5 }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Offline V3 Devices by Firmware
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {mode === "weekly"
                        ? "Weekly"
                        : mode === "monthly"
                          ? "Monthly"
                          : mode === "yearly"
                            ? "Yearly"
                            : mode === "daily"
                              ? "Daily"
                              : "Custom"}
                    </Typography>
                  </Box>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: 220,
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    <Select
                      multiple
                      value={selectedFirmwares}
                      renderValue={(selected) => {
                        if (selected.length === 0) return "None selected";
                        if (selected.length === allFirmwares.length) return "All Firmware";
                        return `${selected.length} selected`;
                      }}
                      sx={{
                        color: "#c0c0c0",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.2)",
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() =>
                          handleSelectAll(selectedFirmwares.length !== allFirmwares.length)
                        }
                      >
                        <Checkbox
                          checked={selectedFirmwares.length === allFirmwares.length}
                          indeterminate={
                            selectedFirmwares.length > 0 &&
                            selectedFirmwares.length < allFirmwares.length
                          }
                        />
                        <ListItemText primary="Select All" />
                      </MenuItem>

                      {allFirmwares.map((firmware) => (
                        <MenuItem
                          key={firmware}
                          onClick={() => handleToggleFirmware(firmware)}
                        >
                          <Checkbox checked={selectedFirmwares.includes(firmware)} />
                          <ListItemText primary={firmware} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Box sx={{ width: "100%", height: 320, mt: 3 }}>
                  {!hasFirmwareOnlineOfflineData ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        No data available
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={filteredFirmwareData} barCategoryGap="20%">
                        <XAxis
                          dataKey="firmware"
                          tick={{ fontSize: 12, fill: "#c0c0c0" }}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 12, fill: "#c0c0c0" }}
                        />
                        {renderFirmwareTooltip()}
                        <Legend
                          formatter={(value) => (
                            <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                              {value}
                            </span>
                          )}
                        />
                        <Bar
                          dataKey="offlineCount"
                          name="Offline Devices"
                          fill="#ff4d4f"
                          radius={[10, 10, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Request Count */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent sx={{ p: 2.25 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3 }}>
                  API Request Count
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flex: 1, mb: 4 }}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={reqFromStr}
                    onChange={(e) => {
                      const value = e.target.value;

                      setReqFromStr(value);
                      fetchRequestCounts(value, reqToStr);
                    }}
                    inputRef={reqFromRef}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      "& input::-webkit-calendar-picker-indicator": {
                        display: "none",
                      },
                      "& input": {
                        appearance: "textfield",
                      },
                    }}
                    inputProps={{
                      onClick: () => openPicker(reqFromRef),
                      style: { cursor: "pointer" },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => openPicker(reqFromRef)}
                            edge="end"
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={reqToStr}
                    onChange={(e) => {
                      const value = e.target.value;

                      setReqToStr(value);
                      fetchRequestCounts(reqFromStr, value);
                    }}
                    inputRef={reqToRef}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      "& input::-webkit-calendar-picker-indicator": {
                        display: "none",
                      },
                      "& input": {
                        appearance: "textfield",
                      },
                    }}
                    inputProps={{
                      onClick: () => openPicker(reqToRef),
                      style: { cursor: "pointer" },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => openPicker(reqToRef)}
                            edge="end"
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="contained" size="small" onClick={fetchRequestCounts} hidden>
                    Refresh
                  </Button>
                </Stack>

                <Box sx={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={processedData}
                      onClick={(e) => {
                        if (e?.activePayload?.length) {
                          const clicked = e.activePayload[0].payload;
                          handleDrillDown(clicked);
                        }
                      }}>
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(t) => requestCountFormatXAxis(t, granularity)}
                      />

                      <YAxis />

                      <RTooltip
                        label="time"
                        labelFormatter={requestCountTooltipLabelFormatter}
                        formatter={(value) => [`${value}`, "Requests"]}
                        contentStyle={{
                          background: "white",
                          border: "1px solid #ccc",
                          borderRadius: 4,
                          padding: 8,
                          color: "#000",
                        }}
                        itemStyle={{
                          color: "#46505A",
                        }}
                        labelStyle={{
                          color: "black",
                          fontWeight: 600,
                        }}
                      />
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="Requests"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Dialog
                  open={drillOpen}
                  onClose={() => setDrillOpen(false)}
                  fullWidth
                  maxWidth="md"
                >
                  <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {drillTitle}
                    <IconButton onClick={() => setDrillOpen(false)}>
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent>
                    <Box sx={{ width: "100%", height: 320 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={drillData}>
                          <XAxis
                            dataKey="time"
                            type="number"
                            scale="time"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(t) => requestCountFormatXAxis(t, drillGranularity)}
                          />
                          <YAxis />
                          <RTooltip
                            label="time"
                            labelFormatter={requestCountTooltipLabelFormatter}
                            formatter={(value) => [`${value}`, "Requests"]}
                            contentStyle={{
                              background: "white",
                              border: "1px solid #ccc",
                              borderRadius: 4,
                              padding: 8,
                              color: "#000",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2.5}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </Grid>

          {/* Auth providers pie */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', }}>
              <CardContent sx={{ p: 2.25 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Users by sign-in method (Current)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Google vs Apple vs Email
                </Typography>

                <Box sx={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <RTooltip />
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />
                      <Pie
                        data={authPie}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={0}
                      >
                        <Cell fill={"#60A5FA"} />
                        <Cell fill={"#A1A1AA"} />
                        <Cell fill={"#F59E0B"} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Device connected pie */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', }}>
              <CardContent sx={{ p: 2.25 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Users with devices (Current)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  With device(s) vs No devices
                </Typography>

                <Box sx={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <RTooltip />
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />
                      <Pie
                        data={deviceConnPie}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={0}
                      >
                        <Cell fill={theme.palette.success.main} />
                        <Cell fill={theme.palette.warning.main} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', }}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      src={mqttLogo}
                      alt="MQTT"
                      variant="rounded"
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "transparent",
                        "& img": {
                          objectFit: "contain",
                        },
                      }}
                    />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        New MQTT Devices
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mode === "weekly"
                          ? "Weekly"
                          : mode === "monthly"
                            ? "Monthly"
                            : mode === "yearly"
                              ? "Yearly"
                              : mode === "daily"
                                ? "Daily"
                                : "Custom"}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                {/* <Divider sx={{ mb: 2 }} /> */}

                <Box sx={{ width: "100%", height: 320, mt: 3 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      {/* <CartesianGrid strokeDasharray="3 3" /> */}
                      <XAxis
                        dataKey="label"
                        tickFormatter={(value) => {
                          const date = new Date(value);

                          if (mode === "monthly") {
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                            });
                          }

                          if (mode === "yearly") {
                            return date.getFullYear();
                          }

                          if (mode === "weekly" || mode === "daily") {
                            return date.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            });
                          }

                          return date.toLocaleDateString();
                        }}
                      />
                      <YAxis />
                      {renderTooltip("New MQTT Devices")}
                      <Legend
                        formatter={(value) => (
                          <span style={{ color: "#c0c0c0", fontWeight: 500, fontSize: 16 }}>
                            {value}
                          </span>
                        )}
                      />
                      <Line
                        type="monotone"
                        dataKey="newMqttDevices"
                        name="New MQTT devices"
                        stroke="#660066"
                        strokeWidth={3}
                        dot={false}
                        activeDot={{ r: 5 }}
                      />

                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
